import { useState, useEffect } from "react";
import { useQuery } from "@tanstack/react-query";
import { getAlerts, getDevices } from "../api/axios";
import { Card, CardContent, CardDescription, CardHeader, CardTitle } from "../components/ui/card";
import { Button } from "../components/ui/button";
import { Badge } from "../components/ui/badge";
import { Table, TableBody, TableCell, TableHead, TableHeader, TableRow } from "../components/ui/table";
import { Select, SelectContent, SelectItem, SelectTrigger, SelectValue } from "../components/ui/select";
import { 
  Bell, 
  AlertTriangle, 
  AlertOctagon, 
  Info, 
  RefreshCw,
  Activity,
  Filter,
  Clock,
  Zap
} from "lucide-react";
import { format, formatDistanceToNow } from "date-fns";

export default function LiveAlerts() {
  const [autoRefresh, setAutoRefresh] = useState(true);
  const [severityFilter, setSeverityFilter] = useState("all");
  const [refreshInterval, setRefreshInterval] = useState(30); // seconds

  // Fetch only open (unresolved and unacknowledged) alerts
  const { 
    data: alerts = [], 
    isLoading: isLoadingAlerts, 
    refetch: refetchAlerts 
  } = useQuery<any[]>({
    queryKey: ["/api/live-alerts", severityFilter],
    queryFn: () => {
      const params: { severity?: string; acknowledged: boolean; resolved: boolean } = {
        acknowledged: false,
        resolved: false
      };
      if (severityFilter !== "all") params.severity = severityFilter;
      return getAlerts(params);
    },
    refetchInterval: autoRefresh ? refreshInterval * 1000 : false,
    refetchIntervalInBackground: true,
  });

  // Fetch devices for device name mapping
  const { data: devices = [] } = useQuery({
    queryKey: ["/api/devices"],
    queryFn: getDevices,
  });

  // Auto-refresh countdown
  const [countdown, setCountdown] = useState(refreshInterval);

  useEffect(() => {
    if (!autoRefresh) return;

    const interval = setInterval(() => {
      setCountdown((prev) => {
        if (prev <= 1) {
          return refreshInterval;
        }
        return prev - 1;
      });
    }, 1000);

    return () => clearInterval(interval);
  }, [autoRefresh, refreshInterval]);

  // Reset countdown when refresh interval changes
  useEffect(() => {
    setCountdown(refreshInterval);
  }, [refreshInterval]);

  // Alert severity badge
  const getSeverityBadge = (severity: string) => {
    switch (severity) {
      case "critical":
        return (
          <Badge variant="destructive" className="flex items-center gap-1 animate-pulse">
            <AlertOctagon className="h-3 w-3" /> 
            Critical
          </Badge>
        );
      case "warning":
        return (
          <Badge className="bg-orange-500 flex items-center gap-1">
            <AlertTriangle className="h-3 w-3" /> 
            Warning
          </Badge>
        );
      case "info":
        return (
          <Badge variant="outline" className="flex items-center gap-1">
            <Info className="h-3 w-3" /> 
            Info
          </Badge>
        );
      default:
        return (
          <Badge variant="outline" className="flex items-center gap-1">
            <Info className="h-3 w-3" /> 
            {severity}
          </Badge>
        );
    }
  };

  // Get device name by id
  const getDeviceName = (deviceId: any) => {
    if (!deviceId) return 'Unknown Device';
    // Handle deviceId as string, ObjectId, or object with _id
    const deviceIdStr = typeof deviceId === 'object' 
      ? (deviceId._id?.toString() || deviceId.toString()) 
      : deviceId.toString();
    const device = devices.find((d: any) => {
      const dId = (d._id || d.id)?.toString();
      return dId === deviceIdStr;
    });
    return device ? device.name : `Device ${deviceIdStr}`;
  };

  // Format timestamp to show time ago
  const formatTimeAgo = (timestamp: string) => {
    try {
      return formatDistanceToNow(new Date(timestamp), { addSuffix: true });
    } catch (e) {
      return "Invalid date";
    }
  };

  // Format full timestamp
  const formatFullTime = (timestamp: string) => {
    try {
      return format(new Date(timestamp), "PPp");
    } catch (e) {
      return "Invalid date";
    }
  };

  // Sort alerts by severity and timestamp (critical first, then by newest)
  const sortedAlerts = [...alerts].sort((a, b) => {
    const severityOrder = { critical: 0, warning: 1, info: 2 };
    const aSeverity = severityOrder[a.severity as keyof typeof severityOrder] ?? 3;
    const bSeverity = severityOrder[b.severity as keyof typeof severityOrder] ?? 3;
    
    if (aSeverity !== bSeverity) {
      return aSeverity - bSeverity;
    }
    
    // If same severity, sort by newest first
    return new Date(b.timestamp).getTime() - new Date(a.timestamp).getTime();
  });

  // Count alerts by severity
  const alertCounts = {
    total: alerts.length,
    critical: alerts.filter(a => a.severity === 'critical').length,
    warning: alerts.filter(a => a.severity === 'warning').length,
    info: alerts.filter(a => a.severity === 'info').length,
  };

  return (
    <div className="container mx-auto py-6">
      <div className="flex justify-between items-center mb-6">
        <div className="flex items-center gap-3">
          <div className="flex items-center gap-2">
            <Activity className="h-8 w-8 text-red-500" />
            <h1 className="text-3xl font-bold">Live Alerts</h1>
          </div>
          {alertCounts.total > 0 && (
            <Badge variant="destructive" className="animate-pulse">
              {alertCounts.total} Active
            </Badge>
          )}
        </div>
        
        <div className="flex items-center gap-3">
          {/* Auto-refresh controls */}
          <div className="flex items-center gap-2 text-sm">
            <Select 
              value={refreshInterval.toString()} 
              onValueChange={(value) => setRefreshInterval(parseInt(value))}
            >
              <SelectTrigger className="w-20 h-8">
                <SelectValue />
              </SelectTrigger>
              <SelectContent>
                <SelectItem value="10">10s</SelectItem>
                <SelectItem value="30">30s</SelectItem>
                <SelectItem value="60">1m</SelectItem>
                <SelectItem value="300">5m</SelectItem>
              </SelectContent>
            </Select>
            <Button
              variant={autoRefresh ? "default" : "outline"}
              size="sm"
              onClick={() => setAutoRefresh(!autoRefresh)}
              className="flex items-center gap-1"
            >
              <Zap className="h-4 w-4" />
              Auto-refresh
              {autoRefresh && <span className="text-xs">({countdown}s)</span>}
            </Button>
          </div>
          
          <Button 
            variant="outline" 
            size="sm" 
            onClick={() => refetchAlerts()}
            className="flex items-center gap-1"
          >
            <RefreshCw className="h-4 w-4" />
            Refresh Now
          </Button>
        </div>
      </div>

      {/* Alert summary cards */}
      <div className="grid grid-cols-1 md:grid-cols-4 gap-4 mb-6">
        <Card>
          <CardContent className="p-4">
            <div className="flex items-center justify-between">
              <div>
                <p className="text-sm text-muted-foreground">Total Active</p>
                <p className="text-2xl font-bold">{alertCounts.total}</p>
              </div>
              <Bell className="h-8 w-8 text-blue-500" />
            </div>
          </CardContent>
        </Card>
        
        <Card>
          <CardContent className="p-4">
            <div className="flex items-center justify-between">
              <div>
                <p className="text-sm text-muted-foreground">Critical</p>
                <p className="text-2xl font-bold text-red-600">{alertCounts.critical}</p>
              </div>
              <AlertOctagon className="h-8 w-8 text-red-500" />
            </div>
          </CardContent>
        </Card>
        
        <Card>
          <CardContent className="p-4">
            <div className="flex items-center justify-between">
              <div>
                <p className="text-sm text-muted-foreground">Warning</p>
                <p className="text-2xl font-bold text-orange-600">{alertCounts.warning}</p>
              </div>
              <AlertTriangle className="h-8 w-8 text-orange-500" />
            </div>
          </CardContent>
        </Card>
        
        <Card>
          <CardContent className="p-4">
            <div className="flex items-center justify-between">
              <div>
                <p className="text-sm text-muted-foreground">Info</p>
                <p className="text-2xl font-bold text-blue-600">{alertCounts.info}</p>
              </div>
              <Info className="h-8 w-8 text-blue-500" />
            </div>
          </CardContent>
        </Card>
      </div>

      {/* Main alerts table */}
      <Card>
        <CardHeader>
          <div className="flex justify-between items-center">
            <div>
              <CardTitle className="flex items-center gap-2">
                <Activity className="h-5 w-5 text-red-500" />
                Active Alerts
              </CardTitle>
              <CardDescription>
                Real-time view of all active alerts requiring attention
                {autoRefresh && ` (Auto-refreshing every ${refreshInterval}s)`}
              </CardDescription>
            </div>
            
            <div className="flex items-center gap-2">
              <Filter className="h-4 w-4 text-muted-foreground" />
              <Select 
                value={severityFilter} 
                onValueChange={setSeverityFilter}
              >
                <SelectTrigger className="w-[130px]">
                  <SelectValue placeholder="All Severities" />
                </SelectTrigger>
                <SelectContent>
                  <SelectItem value="all">All Severities</SelectItem>
                  <SelectItem value="critical">Critical Only</SelectItem>
                  <SelectItem value="warning">Warning Only</SelectItem>
                  <SelectItem value="info">Info Only</SelectItem>
                </SelectContent>
              </Select>
            </div>
          </div>
        </CardHeader>
        
        <CardContent>
          {isLoadingAlerts ? (
            <div className="flex justify-center items-center py-8">
              <RefreshCw className="h-8 w-8 animate-spin text-primary/70" />
            </div>
          ) : sortedAlerts.length === 0 ? (
            <div className="flex flex-col items-center justify-center py-12 text-center">
              <div className="flex items-center gap-2 text-green-600 mb-4">
                <Bell className="h-12 w-12" />
              </div>
              <h3 className="text-lg font-medium text-green-700">All Clear!</h3>
              <p className="text-sm text-muted-foreground mt-1">
                {severityFilter !== "all" 
                  ? `No active ${severityFilter} alerts at this time` 
                  : "No active alerts at this time"}
              </p>
              <div className="mt-4 text-xs text-muted-foreground flex items-center gap-1">
                <Clock className="h-3 w-3" />
                Last updated: {format(new Date(), "HH:mm:ss")}
              </div>
            </div>
          ) : (
            <div className="overflow-x-auto">
              <Table>
                <TableHeader>
                  <TableRow>
                    <TableHead>Severity</TableHead>
                    <TableHead>Device</TableHead>
                    <TableHead>Alert</TableHead>
                    <TableHead>Time</TableHead>
                    <TableHead>Duration</TableHead>
                  </TableRow>
                </TableHeader>
                <TableBody>
                  {sortedAlerts.map((alert: any) => (
                    <TableRow 
                      key={alert._id || alert.id} 
                      className={`
                        ${alert.severity === 'critical' ? 'bg-red-50 border-l-4 border-l-red-500' : ''}
                        ${alert.severity === 'warning' ? 'bg-orange-50 border-l-4 border-l-orange-500' : ''}
                      `}
                    >
                      <TableCell>
                        {getSeverityBadge(alert.severity)}
                      </TableCell>
                      <TableCell className="font-medium">
                        {getDeviceName(alert.deviceId)}
                      </TableCell>
                      <TableCell>
                        <div>
                          <div className="font-medium">
                            {alert.details || alert.type || 'Alert'}
                          </div>
                          <div className="text-sm text-muted-foreground">
                            {alert.message.length > 80 
                              ? `${alert.message.substring(0, 80)}...` 
                              : alert.message}
                          </div>
                        </div>
                      </TableCell>
                      <TableCell className="whitespace-nowrap text-sm">
                        <div title={formatFullTime(alert.timestamp)}>
                          {format(new Date(alert.timestamp), "HH:mm:ss")}
                        </div>
                        <div className="text-xs text-muted-foreground">
                          {format(new Date(alert.timestamp), "MMM dd")}
                        </div>
                      </TableCell>
                      <TableCell className="text-sm">
                        <div className="flex items-center gap-1">
                          <Clock className="h-3 w-3 text-muted-foreground" />
                          {formatTimeAgo(alert.timestamp)}
                        </div>
                      </TableCell>
                    </TableRow>
                  ))}
                </TableBody>
              </Table>
            </div>
          )}
        </CardContent>
      </Card>
    </div>
  );
}